import { ForbiddenError, UnauthorizedError, errorCodes } from "@exceptions";
import { useEndSession } from "@src/hooks/use-end-session";
import { toSafeInternalPath } from "@src/utils/safe-internal-path";
import {
  MutationCacheNotifyEvent,
  QueryCacheNotifyEvent,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  LOGIN_PATH,
  VERIFY_TWO_FA_PATH,
  VERIFY_YOUR_EMAIL,
} from "../modules/main/router/paths";
import { toMemberPath } from "../path";
import { useMe } from "./me";

export function useAuthenticated(onSuccessRedirectTo?: string) {
  const navigate = useNavigate();
  const location = useLocation();

  const queryClient = useQueryClient();
  const endSession = useEndSession();

  useEffect(() => {
    const options = {
      state: { from: onSuccessRedirectTo ?? location.pathname },
    };

    const queryCacheUnsubscribe = queryClient
      .getQueryCache()
      .subscribe((event: QueryCacheNotifyEvent) => {
        if (event.query.state.status === "error" && event.type === "updated") {
          if (event.query.state.error instanceof UnauthorizedError) {
            if (
              event.query.state.error?.errorCode ===
              errorCodes.TWO_FACTOR_AUTH_REQUIRED
            ) {
              endSession(toMemberPath(VERIFY_TWO_FA_PATH()), options);
            } else {
              endSession(toMemberPath(LOGIN_PATH()), options);
            }
          }

          if (
            event.query.state.error instanceof ForbiddenError &&
            event.query.state.error?.errorCode ===
              errorCodes.EMAIL_VERIFICATION_REQUIRED
          ) {
            navigate(toMemberPath(VERIFY_YOUR_EMAIL()), {
              state: { from: onSuccessRedirectTo ?? location.pathname },
            });
          }
        }
      });

    const mutationCacheUnsubscribe = queryClient
      .getMutationCache()
      .subscribe((event: MutationCacheNotifyEvent) => {
        if (
          event?.mutation?.state.status === "error" &&
          event.mutation.state.error instanceof UnauthorizedError
        ) {
          if (
            event.mutation.state.error?.errorCode ===
            errorCodes.TWO_FACTOR_AUTH_REQUIRED
          ) {
            endSession(toMemberPath(VERIFY_TWO_FA_PATH()), options);
          } else {
            endSession(toMemberPath(LOGIN_PATH()), options);
          }
        }
      });

    return () => {
      queryCacheUnsubscribe();
      mutationCacheUnsubscribe();
    };
  }, [
    queryClient,
    endSession,
    navigate,
    location.pathname,
    onSuccessRedirectTo,
  ]);
}

export function useMeAuthenticated() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from?: string } | undefined;

  const {
    isSuccess: meIsSuccess,
    isError: meIsError,
    error: meError,
    dataUpdatedAt: meUpdatedAt,
    isLoading: meIsLoading,
  } = useMe();

  useEffect(() => {
    if (meIsSuccess && !state?.from) {
      navigate(`/`);
    } else if (meIsSuccess && state?.from) {
      navigate(toSafeInternalPath(state.from));
    } else if (meIsError) {
      if (meError?.errorCode === errorCodes.EMAIL_VERIFICATION_REQUIRED) {
        navigate(toMemberPath(VERIFY_YOUR_EMAIL()), {
          state: { from: state?.from },
        });
      } else if (meError?.errorCode === errorCodes.TWO_FACTOR_AUTH_REQUIRED) {
        navigate(toMemberPath(VERIFY_TWO_FA_PATH()), {
          state: { from: state?.from },
        });
      }
    }
  }, [meUpdatedAt, meIsError]);

  return {
    meIsSuccess,
    meIsError,
    meError,
    meUpdatedAt,
    meIsLoading,
  };
}
